import React, { useCallback } from 'react'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { CaptionSM } from '@/atoms/Text'
import { paths } from '@/constants'
import { saveGuildContinueWatching } from '@/services/GuildContinueWatching'
import { useGuildUser } from '@/services/GuildUserService'
import { Episode } from '@/services/ProjectsService'
import { Trailer } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

interface LoginToWatchButtonGroupProps {
  episode?: Episode | null
  isGuildMemberContent: boolean
  slug: string
  trailer?: Trailer
}

function areDatesEqual(date1?: Date, date2?: Date) {
  if (!date1 || !date2) return false
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}
export const LoginToWatchButtonGroup: React.FC<LoginToWatchButtonGroupProps> = ({
  episode,
  isGuildMemberContent,
  slug,
  trailer,
}) => {
  const now = new Date()
  const earlyAccessAvailableDate = episode?.earlyAccessDate ? new Date(episode?.earlyAccessDate) : undefined
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const { isGuildMember } = useGuildUser()
  const track = useSafeTrack()

  const handleJoinToWatchClicked = useCallback(() => {
    track('Clicked Join to Watch', { episode })
    if (episode) saveGuildContinueWatching(episode, track, locale)
  }, [episode, track, locale])

  const availableDateTime = areDatesEqual(earlyAccessAvailableDate, new Date())
    ? Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(earlyAccessAvailableDate)
    : earlyAccessAvailableDate?.toLocaleDateString()

  return (
    <div className="flex flex-col md:flex-row">
      <When condition={isGuildMemberContent && !isGuildMember}>
        {earlyAccessAvailableDate && earlyAccessAvailableDate <= now ? (
          <LinkButton
            className="box-border h-8 min-h-[50px] w-full rounded-md backdrop-blur-sm md:w-fit"
            href={`${paths.guild.join}/${slug}`}
            onClick={handleJoinToWatchClicked}
            variant="white"
          >
            <div className="mr-2">
              <GuildIcon size={24} />
            </div>
            <CaptionSM weight="bold" className="text-base">
              {t('joinToWatch', 'Join To Watch')}
            </CaptionSM>
          </LinkButton>
        ) : (
          <Button
            className="box-border h-8 min-h-[50px] w-full cursor-not-allowed rounded-md backdrop-blur-sm md:w-fit"
            variant="white"
          >
            <div className="mr-2">
              <GuildIcon size={24} />
            </div>
            <CaptionSM weight="bold" className="text-base">
              {t('availableDate', 'Available {{date}}', { date: availableDateTime })}
            </CaptionSM>
          </Button>
        )}
      </When>
      {Boolean(trailer?.id) && (
        <LinkButton
          href={`/watch/${slug}/videos/trailer/${trailer?.id}`}
          variant="white"
          outline
          className="mt-4 box-border h-8 min-h-[50px] w-full rounded-md backdrop-blur-sm md:ml-4 md:mt-0 md:w-fit"
        >
          <CaptionSM weight="bold" className="text-base">
            {t('watchTrailer', 'Watch Trailer')}
          </CaptionSM>
        </LinkButton>
      )}
    </div>
  )
}
